import { Alert, Box, Button, Paper, Typography } from "@mui/material";

import type { AuthUser } from "@/auth/helloCashflowDomain/auth.types";
import { adminUiCopy } from "@/copy/adminUiCopy";
import { copyToClipboard } from "@/utils/subscriptionHelpers";

interface AdminAccessDeniedPanelProps {
  isLoggingOut: boolean;
  onLogout: () => void;
  user: AuthUser;
}

const AdminAccessDeniedPanel = ({
  isLoggingOut,
  onLogout,
  user,
}: AdminAccessDeniedPanelProps) => {
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
        textAlign: "center",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderColor: "divider",
          borderRadius: 2,
          borderStyle: "solid",
          borderWidth: 1,
          maxWidth: 560,
          p: 4,
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {adminUiCopy.authAccess.deniedTitle}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mt: 1,
          }}
        >
          {adminUiCopy.authAccess.deniedSubtitle}
        </Typography>
        <Alert severity="info" sx={{ mt: 3, textAlign: "left" }}>
          {adminUiCopy.authAccess.deniedHelp}
        </Alert>

        <Box sx={{ mt: 3, textAlign: "left" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {adminUiCopy.authAccess.signedInAsTitle}
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {adminUiCopy.authAccess.nameLabel}:
            </Box>{" "}
            {user.displayName ?? adminUiCopy.common.notSet}
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {adminUiCopy.authAccess.emailLabel}:
            </Box>{" "}
            {user.email ?? adminUiCopy.common.notAvailable}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1,
            }}
          >
            {adminUiCopy.authAccess.signOutHint}
          </Typography>
          <Button
            variant="contained"
            color="error"
            sx={{ mt: 2 }}
            disabled={isLoggingOut}
            onClick={onLogout}
          >
            {adminUiCopy.common.signOut}
          </Button>
        </Box>

        <Box sx={{ mt: 3, textAlign: "left" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {adminUiCopy.authAccess.userIdLabel}
          </Typography>
          <Box
            component="code"
            sx={{
              bgcolor: "action.hover",
              borderRadius: 1,
              display: "block",
              fontFamily: "monospace",
              fontSize: 12,
              p: 1.5,
              userSelect: "all",
              wordBreak: "break-all",
            }}
          >
            {user.uid}
          </Box>
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
            onClick={() => {
              void copyToClipboard(user.uid);
            }}
          >
            {adminUiCopy.common.copyUserId}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminAccessDeniedPanel;
